/**
 * Framework-free event emitter for MFA-required events.
 * Bridges the axios interceptor (non-React) to the MFA provider (React).
 * Ported from shipstation-lite/src/features/mfa/mfa-emitter.ts
 */

import type { MfaRetryRequest } from './mfa-types';

type MfaRetryHandler = (request: MfaRetryRequest) => void;

class MfaEmitterImpl {
  private listeners: Set<MfaRetryHandler> = new Set();

  /**
   * Subscribe to MFA-required events.
   * Returns an unsubscribe function.
   */
  subscribe(handler: MfaRetryHandler): () => void {
    this.listeners.add(handler);
    return () => {
      this.listeners.delete(handler);
    };
  }

  /**
   * Emit an MFA-required event to all subscribers.
   * Returns false when there were no subscribers, so callers holding a
   * pending Promise can settle it instead of hanging forever.
   */
  emit(request: MfaRetryRequest): boolean {
    this.listeners.forEach((handler) => handler(request));
    return this.listeners.size > 0;
  }
}

/**
 * Singleton MFA emitter.
 */
export const mfaRequiredEmitter = new MfaEmitterImpl();
